import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	ViewEncapsulation,
	forwardRef,
	ViewChild,
	ElementRef,
	AfterViewInit,
	OnDestroy,
	SimpleChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { IdService, formatNumber, langTypes, FormControlBase } from '../../shared/index';
import { TInputSize, TInputSizeWidth, TInputType, TInputUnitPosition, IInputTextUpdateEvent } from './input.types';

export const INPUT_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => InputComponent),
	multi: true
};
@Component({
	selector: 'rbc-input',
	templateUrl: './input.component.html',
	styleUrls: ['./input.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [INPUT_VALUE_ACCESSOR]
})
export class InputComponent extends FormControlBase<string> implements OnInit, OnDestroy, AfterViewInit {
	// Private Members
	defaultError = 'Invalid';
	focused = false;

	// Input Parameters
	@Input() id: string;
	@Input() placeholder: string;
	@Input() label: string;
	@Input() name: string;
	@Input() helpText: string;
	@Input() errorLabel: string;
	@Input() unit: string;
	@Input() validateRegex: string;
	@Input() value: string = '';
	@Input() ariaAttributes: object;
	@Input() size: TInputSize = 'small';
	@Input() sizeWidth: TInputSizeWidth;
	@Input() type: TInputType = 'text';
	@Input() search = false;
	@Input() secure = false;
	@Input() required = false;
	@Input() noWidth = false;
	@Input() disabled = false;
	@Input() charCount: string | number;
	@Input() unitPosition: TInputUnitPosition = 'right';
	@Input() formatNumberLive = false;
	@Input() formatLang: langTypes = 'en';
	@Input() displayError = true;
	@Input() displayErrorColor = true;
	@Input() ariaDescribedBy: string;

	// Output Parameters
	@Output() valueChange = new EventEmitter<string>();
	@Output() inputTextUpdate = new EventEmitter<IInputTextUpdateEvent>();
	@Output() onInput = new EventEmitter<string>();
	@Output() focusOut = new EventEmitter<string>();
	@Output() clear = new EventEmitter<string>();

	// Element Refs
	@ViewChild('inputElm') inputElm: ElementRef;
	@ViewChild('inputModel') inputElmControl: NgModel; // gets the ngModel
	@ViewChild('error_label') slotError: ElementRef; // gets the ngModel

	constructor(private idService: IdService) {
		super();
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	ngOnInit() {
		this.id = this.id ? this.id : this.idService.generate();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);

		if (changes && changes.value) {
			this.writeValue(changes.value.currentValue);
		}
	}

	ngAfterViewInit() {
		// If the error slot is used then add the error class to the element containing the error
		// ng-content
		if (this.slotError.nativeElement.childElementCount !== 0) {
			this.slotError.nativeElement.classList.add('error');
		}
	}

	// Implemented from ControlValueAccessor interface
	writeValue(text: any): void {
		this.innerValue = text;
	}

	typeGen() {
		if (this.focused && this.formatNumberLive) {
			return 'number';
		} else if (this.secure) {
			return 'password';
		} else if (this.formatNumberLive) {
			return 'text';
		} else if (this.unit) {
			return 'number';
		} else if (this.type) {
			return this.type;
		} else {
			return 'text';
		}
	}

	calculateClasses() {
		const classes = {};
		classes['input-wrap'] = true;
		classes['input-unit'] = this.unit;

		// unit position
		classes['input-unit-left'] = this.unit && this.unitPosition === 'left';
		classes['input-unit-right'] = this.unit && this.unitPosition === 'right';

		// input size
		switch (this.size) {
			case 'small':
				classes['input-small'] = true;
				break;
			case 'medium':
				classes['input-medium'] = true;
				break;
			case 'large':
				classes['input-large'] = true;
		}

		// width size
		switch (this.sizeWidth) {
			case 'small':
				classes['input-width-small'] = true;
				break;
			case 'xsmall':
				classes['input-width-xsmall'] = true;
				break;
			case 'auto':
				classes['input-width-auto'] = true;
		}

		// no width
		classes['no-width'] = this.noWidth;

		// input icon wrapper
		classes['input-icon-wrapper'] = this.typeGen() === 'search';

		return classes;
	}

	replaceAndFormatNumberLive(e) {
		if (!this.formatNumberLive) {
			return;
		}

		let plainNumber = 0;
		let nbDecimal = 0;
		const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '.', '-'];
		let text = this.innerValue.toString();
		text = text === 'NaN' ? '' : text;
		if (typeof e !== 'undefined' && numbers.indexOf(e.key) === -1) {
			text = text.replace(e.key, '');
		}
		if (text !== '' && text !== '-') {
			if (
				typeof e !== 'undefined' &&
				e.key !== '.' &&
				(typeof this.formatLang === 'undefined' || this.formatLang === 'en')
			) {
				plainNumber = parseFloat(text.replace(/,/g, ''));
				nbDecimal = plainNumber.toString().split('.').length > 1 ? plainNumber.toString().split('.')[1].length : 0;
				text = formatNumber(plainNumber.toString(), 'en', nbDecimal);
			} else if (
				typeof e !== 'undefined' &&
				e.key !== ',' &&
				typeof this.formatLang !== 'undefined' &&
				this.formatLang === 'fr'
			) {
				plainNumber = parseFloat(text.replace(/\xa0/g, '')); // clean spaces
				plainNumber = parseFloat(plainNumber.toString().replace(/,/g, '.')); // switch back to periods, if any
				nbDecimal = plainNumber.toString().split('.').length > 1 ? plainNumber.toString().split('.')[1].length : 0;
				text = formatNumber(plainNumber.toString(), this.formatLang, nbDecimal);
			}
		}

		this.inputElm.nativeElement.setAttribute('type', 'text');
		this.inputElm.nativeElement.value = text;
	}

	onFocus(e) {
		this.focused = true;
		this.inputElm.nativeElement.value = this.innerValue;
	}

	clearInput() {
		this.setModel('');
		this.clear.emit(this.innerValue);
	}

	onBlur(e) {
		const hasError = this.checkForErrors(e);
		this.inputElmControl.control.setErrors(hasError && this.inputElm ? { regex: true } : null);
		this.focused = false;
		this.replaceAndFormatNumberLive(e);
		this.setModel(e);
		this.touch();
		this.focusOut.emit(this.innerValue);
	}

	onKeyUp(e) {
		// Emit if not enter or escape
		if (e.keyCode !== 13 && e.keyCode !== 27) {
			this.onInput.emit(this.inputElm.nativeElement.value);
			this.valueChange.emit(this.inputElm.nativeElement.value);
		}
	}

	checkForErrors(val: string | number) {
		if (this.validateRegex && this.validateRegex !== '') {
			const regex = new RegExp(this.validateRegex);
			return !regex.test(val.toString());
		} else {
			return false;
		}
	}

	describedbyGen() {
		if (this.errorLabel || this.helpText || this.ariaDescribedBy || typeof this.unit != 'undefined') {
			const descriptors = {
				[this.id + 'error']: this.errorLabel,
				[this.id + 'HelpText']: this.helpText,
				[this.ariaDescribedBy]: this.ariaDescribedBy,
				[this.id + 'Unit']: typeof this.unit != 'undefined'
			};
			return Object.keys(descriptors)
				.filter(key => descriptors[key])
				.join(' ');
		} else {
			return;
		}
	}
}
